import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// Two header "slots" that pages can inject content into. The provider is mounted
// by AppShell so both the header (which reads the slots via the outlets) and the
// routed pages (which set them via useHeaderSlot) live under it. Pages that don't
// set the slots leave the header unchanged.
//   - left:  after the logo (breadcrumb / page-switch toggle)
//   - right: before the user menu (e.g. the project cost pill)

type SetContent = (node: ReactNode) => void;

const HeaderSlotContext = createContext<{
  left: ReactNode;
  right: ReactNode;
  setLeft: SetContent;
  setRight: SetContent;
} | null>(null);

export function HeaderSlotProvider({ children }: { children: ReactNode }) {
  const [left, setLeft] = useState<ReactNode>(null);
  const [right, setRight] = useState<ReactNode>(null);
  return (
    <HeaderSlotContext.Provider value={{ left, right, setLeft, setRight }}>
      {children}
    </HeaderSlotContext.Provider>
  );
}

export function HeaderSlotOutlet() {
  const ctx = useContext(HeaderSlotContext);
  return <>{ctx?.left ?? null}</>;
}

export function HeaderSlotRightOutlet() {
  const ctx = useContext(HeaderSlotContext);
  return <>{ctx?.right ?? null}</>;
}

// Render `left`/`right` into the header slots while the calling page is mounted.
// `deps` should include anything the nodes depend on so they refresh on change.
export function useHeaderSlot(left: ReactNode, right: ReactNode, deps: React.DependencyList) {
  const ctx = useContext(HeaderSlotContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setLeft(left);
    ctx.setRight(right);
    return () => { ctx.setLeft(null); ctx.setRight(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
